import React, { useEffect, useState } from 'react'
import CalendarComponent from './CalendarComponentServices'
import { useNotifications } from '@/modules/Notifications'
import { DisponibilityCalendar } from '@/components/PetSitter/EmtCalendar/DisponibilityCalendar'

// API
import { useQuery } from '@tanstack/react-query'
import {
  addEventAsync,
  deleteEventAsync,
  getCalendarAsync,
  getPetsitterCalendarAsync,
  updateMainEventAsync,
} from '@/modules/PetSitter/calendarServiceHelper'

import { DateTime } from 'luxon'
import { CalendarData, CalendarDay, CalendarEvent } from '@/data/PetSitter/Calendar'
import useCurrentUser from '@/hooks/useCurrentUser'

interface Range {
  uuid?: string
  start: Date
  end: Date
  type: string
}

interface Props {
  viewOnly?: boolean
  id?: string
}

const Calendar: React.FC<Props> = ({ viewOnly = false, id }) => {
  const { isOwner, currentPetsitter } = useCurrentUser()
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())
  const [ranges, setRanges] = useState<Range[]>([])
  const [addNotificationError, addNotificationSuccess] = useNotifications()
  const [data, setData] = useState<CalendarData>({} as CalendarData)
  const [loading, setLoading] = useState<{
    add: boolean
    remove: boolean
    fetch: boolean
    delete: Set<string>
  }>({
    add: false,
    remove: false,
    fetch: false,
    delete: new Set(),
  })

  // Local mainEvent for optimistic updates
  const [localMainEvent, setLocalMainEvent] = useState<CalendarEvent | null>({
    '@id': '/.well-known/genid/main-event',
    '@type': 'CalendarEvent',
    uuid: 'main-event',
    calendarEventType: 'ALL_SERVICES_MAIN_EVENT_AVAILABILITY',
    isScheduled: true,
    scheduleEveryMonday: true,
    scheduleEveryTuesday: true,
    scheduleEveryWednesday: true,
    scheduleEveryThursday: true,
    scheduleEveryFriday: true,
    scheduleEverySaturday: true,
    scheduleEverySunday: true,
  })
  const [computedDays, setComputedDays] = useState<CalendarDay[]>([])

  useQuery(
    ['servicesCalendarData', id],
    async (): Promise<CalendarData> => {
      if (id && (viewOnly || isOwner)) {
        return (await getPetsitterCalendarAsync(id, undefined, viewOnly)) as CalendarData
      } else if (currentPetsitter) {
        return (await getCalendarAsync()) as CalendarData
      } else {
        return {} as CalendarData
      }
    },
    {
      onSuccess: (fetchedData) => {
        setData(fetchedData)
        // Reset localMainEvent to the newly fetched mainEvent
        if (!localMainEvent) {
          setLocalMainEvent(fetchedData.mainEvent)
        }
      },
      onError: (error) => {
        console.error('Error fetching calendar data:', error)
      },
      refetchOnWindowFocus: false,
    },
  )

  useEffect(() => {
    if (data && data.events) {
      const newRanges: Range[] = data.events.map((event: CalendarEvent) => ({
        uuid: event.uuid,
        start: event.startDate ? DateTime.fromISO(event.startDate).toJSDate() : new Date(),
        end: event.endDate ? DateTime.fromISO(event.endDate).toJSDate() : new Date(),
        type: event.calendarEventType,
      }))
      setRanges(newRanges)
    }
  }, [data.events])

  // Compute days from localMainEvent and events
  useEffect(() => {
    const generateDaysForRange = (
      startDate: DateTime,
      endDate: DateTime,
      mainEvent: CalendarEvent,
      events: CalendarEvent[],
    ): CalendarDay[] => {
      const allDays: { [date: string]: CalendarDay } = {}
      const today = DateTime.local().startOf('day')

      const daysAvailabilityMap: { [key: number]: boolean } = {
        1: mainEvent.scheduleEveryMonday,
        2: mainEvent.scheduleEveryTuesday,
        3: mainEvent.scheduleEveryWednesday,
        4: mainEvent.scheduleEveryThursday,
        5: mainEvent.scheduleEveryFriday,
        6: mainEvent.scheduleEverySaturday,
        0: mainEvent.scheduleEverySunday,
      }

      const unavailableRanges = (events || [])
        .filter((e) => e.calendarEventType === 'UNAVAILABILITY')
        .map((e) => ({
          start: e.startDate ? DateTime.fromISO(e.startDate).startOf('day') : DateTime.local(),
          end: e.endDate ? DateTime.fromISO(e.endDate).startOf('day') : DateTime.local(),
        }))

      const allServicesEvents = (events || [])
        .filter((e) => e.calendarEventType === 'ALL_SERVICES_AVAILABILITY')
        .map((e) => ({
          start: e.startDate ? DateTime.fromISO(e.startDate).startOf('day') : DateTime.local(),
          end: e.endDate ? DateTime.fromISO(e.endDate).startOf('day') : DateTime.local(),
        }))

      const isInUnavailableRange = (date: DateTime) => unavailableRanges.some((r) => date >= r.start && date <= r.end)

      const isAvailableRange = (date: DateTime) => allServicesEvents.some((r) => date >= r.start && date <= r.end)

      let current = startDate.startOf('month')

      while (current <= endDate.endOf('month')) {
        const daysInMonth = current.daysInMonth || 0

        for (let day = 1; day <= daysInMonth; day++) {
          const currentDate = current.set({ day }).startOf('day')
          const dayOfWeek = currentDate.weekday % 7 // Luxon: Monday=1, Sunday=7 -> 0=Sunday
          const formattedDate = currentDate.toISODate() // 'YYYY-MM-DD'

          if (currentDate < today) {
            // Skip past dates
            continue
          }

          if (isInUnavailableRange(currentDate)) {
            // Mark as unavailable
            if (formattedDate) {
              allDays[formattedDate] = {
                '@type': 'CalendarDay',
                '@id': `/.well-known/genid/${formattedDate}`,
                type: 'UNAVAILABILITY',
                isStart: false,
                isEnd: false,
                dateTime: formattedDate,
              }
            }
          } else if (isAvailableRange(currentDate)) {
            // Mark as available
            if (formattedDate) {
              allDays[formattedDate] = {
                '@type': 'CalendarDay',
                '@id': `/.well-known/genid/${formattedDate}`,
                type: 'AVAILABILITY',
                isStart: false,
                isEnd: false,
                dateTime: formattedDate,
              }
            }
          } else if (
            daysAvailabilityMap[dayOfWeek] &&
            !isInUnavailableRange(currentDate) &&
            !isAvailableRange(currentDate)
          ) {
            // Mark as available
            if (formattedDate) {
              allDays[formattedDate] = {
                '@type': 'CalendarDay',
                '@id': `/.well-known/genid/${formattedDate}`,
                type: 'ALL_SERVICES_MAIN_EVENT_AVAILABILITY',
                isStart: false,
                isEnd: false,
                dateTime: formattedDate,
              }
            }
          }
        }

        // Move to the next month
        current = current.plus({ months: 1 })
      }

      // Convert to array and sort
      const allDaysArray = Object.values(allDays).sort((a, b) => {
        const dateA = DateTime.fromISO(a.dateTime)
        const dateB = DateTime.fromISO(b.dateTime)
        return dateA.toMillis() - dateB.toMillis()
      })

      // Mark isStart / isEnd for consecutive days with the same type
      for (let i = 0; i < allDaysArray.length; i++) {
        const currentDay = DateTime.fromISO(allDaysArray[i].dateTime).startOf('day')
        const prevDay = i > 0 ? DateTime.fromISO(allDaysArray[i - 1].dateTime).startOf('day') : null
        const nextDay =
          i < allDaysArray.length - 1 ? DateTime.fromISO(allDaysArray[i + 1].dateTime).startOf('day') : null

        const prevType = i > 0 ? allDaysArray[i - 1].type : null
        const currentType = allDaysArray[i].type
        const nextType = i < allDaysArray.length - 1 ? allDaysArray[i + 1].type : null

        // Determine if current day is the start of a new range
        const isRangeStart = !prevDay || !prevDay.plus({ days: 1 }).equals(currentDay) || prevType !== currentType

        // Determine if current day is the end of the current range
        const isRangeEnd = !nextDay || !currentDay.plus({ days: 1 }).equals(nextDay) || nextType !== currentType

        if (isRangeStart) {
          allDaysArray[i].isStart = true
        }
        if (isRangeEnd) {
          allDaysArray[i].isEnd = true
        }
      }

      return allDaysArray
    }

    // Calculate the start and end dates using Luxon
    const currentMonthDate = DateTime.fromJSDate(currentMonth)
    const startDate = currentMonthDate.minus({ months: 2 }).startOf('month')
    const endDate = currentMonthDate.plus({ months: 2 }).endOf('month')

    if (localMainEvent) {
      const newComputedDays = generateDaysForRange(startDate, endDate, localMainEvent, data.events)
      setComputedDays(newComputedDays)
    }
  }, [currentMonth, localMainEvent, data.events])

  const handleAddRange = async (newRange: Range, slug: string) => {
    if (slug === 'ALL_SERVICES_AVAILABILITY') {
      setLoading((prev) => ({ ...prev, add: true }))
    } else if (slug === 'UNAVAILABILITY') {
      setLoading((prev) => ({ ...prev, remove: true }))
    }

    try {
      const startDate = DateTime.fromJSDate(newRange.start)
        .setZone('Europe/Paris')
        .startOf('day')
        .toISO({ suppressMilliseconds: false })

      const endDate = DateTime.fromJSDate(newRange.end)
        .setZone('Europe/Paris')
        .endOf('day')
        .toISO({ suppressMilliseconds: false })

      const dataResponse = await addEventAsync(startDate!, endDate!, slug)

      setData(dataResponse as CalendarData)

      addNotificationSuccess({
        title: 'Profil mis à jour',
        message: 'Les modifications ont été enregistrées avec succès',
      })
    } catch (error) {
      addNotificationError({ error })
    }

    setLoading((prev) => ({ ...prev, add: false, remove: false }))
  }

  const handleDeleteRange = async (rangeToDelete: Range) => {
    if (!rangeToDelete.uuid) return

    // Add the UUID to the delete loading set
    setLoading((prev) => {
      const newDeleteSet = new Set(prev.delete)
      if (rangeToDelete.uuid) {
        newDeleteSet.add(rangeToDelete.uuid)
      }
      return { ...prev, delete: newDeleteSet }
    })

    try {
      const dataResponse = await deleteEventAsync(rangeToDelete.uuid)
      setData(dataResponse as CalendarData)
      addNotificationSuccess({
        title: 'Profil mis à jour',
        message: 'Les modifications ont été enregistrées avec succès',
      })
    } catch (error) {
      addNotificationError({ error })
    } finally {
      // Remove the UUID from the delete loading set
      setLoading((prev) => {
        const newDeleteSet = new Set(prev.delete)
        if (rangeToDelete.uuid) {
          newDeleteSet.delete(rangeToDelete.uuid)
        }
        return { ...prev, delete: newDeleteSet }
      })
    }
  }

  const onUpdateMainEvent = async (updatedSchedule: Partial<CalendarEvent>) => {
    if (!localMainEvent) return
    const prevMainEvent = { ...localMainEvent }

    // Optimistic update
    const updatedMain = { ...localMainEvent, ...updatedSchedule }
    setLocalMainEvent(updatedMain)

    try {
      const dataResponse = await updateMainEventAsync(updatedSchedule)
      setData(dataResponse as CalendarData)
      // success, do nothing
    } catch (error) {
      addNotificationError({ error })
      // revert
      setLocalMainEvent(prevMainEvent)
    }
  }

  const calendarData = {
    days: computedDays,
    presets: data.presets || [],
    mainEvent: localMainEvent || ({} as CalendarEvent),
  }

  return (
    <div id="calendar">
      {viewOnly ? (
        <>
          <DisponibilityCalendar
            viewOnly={viewOnly}
            days={calendarData.days}
            currentMonth={currentMonth}
            setCurrentMonth={setCurrentMonth}
          />
        </>
      ) : (
        <>
          {data && (
            <CalendarComponent
              calendar={calendarData}
              ranges={ranges}
              onAddRange={handleAddRange}
              onDeleteRange={handleDeleteRange}
              loading={loading}
              currentMonth={currentMonth}
              setCurrentMonth={setCurrentMonth}
              onUpdateMainEvent={onUpdateMainEvent}
            />
          )}
        </>
      )}
    </div>
  )
}

export default Calendar
